// FassalkartAI — UI string catalogue (CONTRACT §3.5)
//
// The compiled `T` object in translations.js is, and stays, the instant
// offline source of truth: 16 languages x 233 keys shipped in the bundle.
// This module layers *optional* Firestore overrides (`ki_uiStrings`) on top,
// so an admin can fix a wording mistake from the dashboard and have farmers
// see it without a redeploy.
//
// Guarantees, in this order of importance:
//   1. If Firestore is unreachable, slow, empty, or permission-denied, every
//      lookup still returns the compiled value. Nothing blanks out, nothing
//      throws, nothing blocks render.
//   2. Overrides survive a reload offline via a versioned localStorage cache.
//   3. One collection read per session, then a live subscription.
//
// Firestore doc shape (contract §3.5), doc id = `<namespace>__<key>`:
//   { namespace, key, value, sourceLang:'en', i18n: { hi: { value }, ... } }

import { useCallback, useSyncExternalStore } from 'react';
import { collection, limit, onSnapshot, query } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  T,
  getBaseCode,
  getFallbackChain,
  getLanguage,
  subscribeLanguage
} from './languages';

const COLLECTION = 'ki_uiStrings';
const CACHE_KEY = 'fassalkart_uiStrings';
const CACHE_SCHEMA = 1;            // bump to invalidate every client cache
const CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;   // 30 days
const MAX_DOCS = 3000;             // bound on the Firestore read
const FIRST_LOAD_TIMEOUT_MS = 15000;

// ─── Module state ─────────────────────────────────
// overrides: { [namespace]: { [key]: { [lang]: string } } }
let overrides = Object.create(null);
let version = 0;                   // bumps on every override change
let stamp = null;                  // catalogue version stamp (docs+updatedAt)
let status = 'idle';               // idle|cache|live|error|timeout
let started = false;
let unsubscribe = null;
let timeoutId = null;

const listeners = new Set();

function emit() {
  version += 1;
  listeners.forEach(fn => {
    try {
      fn(version);
    } catch (e) {
      console.error('[uiStrings] listener failed:', e);
    }
  });
}

function hasStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
}

// ─── Path helpers ─────────────────────────────────
// Keys may be dotted for nested catalogue entries, e.g.
// getString('cropDoctor', 'severity.mild').

function walk(obj, namespace, key) {
  if (!obj || typeof obj !== 'object') return undefined;
  let node = obj[namespace];
  if (node === undefined || typeof key !== 'string') return undefined;
  for (const part of key.split('.')) {
    if (node === null || typeof node !== 'object') return undefined;
    node = node[part];
    if (node === undefined) return undefined;
  }
  return typeof node === 'string' ? node : undefined;
}

// ─── Cache ────────────────────────────────────────

function readCache() {
  if (!hasStorage()) return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== CACHE_SCHEMA || !parsed.data) return null;
    if (typeof parsed.savedAt === 'number' &&
        Date.now() - parsed.savedAt > CACHE_MAX_AGE_MS) {
      try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
      return null;
    }
    return parsed;
  } catch {
    // Corrupt cache — never let it break startup.
    try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
    return null;
  }
}

function writeCache(data, nextStamp) {
  if (!hasStorage()) return;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      v: CACHE_SCHEMA,
      savedAt: Date.now(),
      stamp: nextStamp,
      data
    }));
  } catch {
    // Quota exceeded / private mode. Overrides stay in memory for this
    // session; next session falls back to the compiled T. Acceptable.
  }
}

// ─── Snapshot -> overrides ────────────────────────

function toMillis(value) {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value.toMillis === 'function') {
    try { return value.toMillis(); } catch { return 0; }
  }
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

/**
 * Fold one `ki_uiStrings` document into the override map.
 * Tolerates every field being missing — bad rows are skipped, not fatal.
 */
function foldDoc(target, id, data) {
  if (!data || typeof data !== 'object') return 0;

  const sep = typeof id === 'string' ? id.indexOf('__') : -1;
  const namespace = typeof data.namespace === 'string' && data.namespace
    ? data.namespace
    : (sep > 0 ? id.slice(0, sep) : null);
  const key = typeof data.key === 'string' && data.key
    ? data.key
    : (sep > 0 ? id.slice(sep + 2) : null);

  if (!namespace || !key) return 0;

  const bucket = target[namespace] || (target[namespace] = Object.create(null));
  const entry = bucket[key] || (bucket[key] = Object.create(null));

  const sourceLang = typeof data.sourceLang === 'string' ? data.sourceLang : 'en';
  if (typeof data.value === 'string' && data.value) {
    entry[sourceLang] = data.value;
  }

  const i18n = data.i18n;
  if (i18n && typeof i18n === 'object') {
    for (const lang of Object.keys(i18n)) {
      const row = i18n[lang];
      if (!row || typeof row !== 'object') continue;
      if (typeof row.value === 'string' && row.value) entry[lang] = row.value;
    }
  }
  return 1;
}

function applySnapshot(snap) {
  const next = Object.create(null);
  let kept = 0;
  let latest = 0;

  snap.forEach(d => {
    const data = d.data();
    kept += foldDoc(next, d.id, data);
    const ts = toMillis(data && data.translationStatus && data.translationStatus.updatedAt);
    if (ts > latest) latest = ts;
  });

  const nextStamp = `${kept}:${latest}`;
  const changed = nextStamp !== stamp;

  overrides = next;
  stamp = nextStamp;
  status = 'live';

  if (changed) writeCache(next, nextStamp);
  emit();
}

// ─── Lifecycle ────────────────────────────────────

/**
 * Start the catalogue: hydrate from localStorage synchronously, then attach a
 * live Firestore subscription. Idempotent and session-scoped — call it once
 * from the app root (LanguageProvider does this).
 *
 * Never throws and never returns a rejected promise.
 */
export function initUiStrings() {
  if (started) return;
  started = true;

  const cached = readCache();
  if (cached) {
    overrides = cached.data;
    stamp = cached.stamp || null;
    status = 'cache';
    emit();
  }

  // onSnapshot has no timeout of its own. This does not abort the listener
  // (it will still deliver whenever the farmer regains signal) — it just
  // stops us reporting "loading" forever on a dead rural connection.
  timeoutId = setTimeout(() => {
    timeoutId = null;
    if (status === 'idle') status = 'timeout';
  }, FIRST_LOAD_TIMEOUT_MS);

  try {
    unsubscribe = onSnapshot(
      query(collection(db, COLLECTION), limit(MAX_DOCS)),
      (snap) => {
        if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
        try {
          applySnapshot(snap);
        } catch (e) {
          console.error('[uiStrings] snapshot parse failed:', e);
          status = 'error';
        }
      },
      (err) => {
        if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
        // Offline / rules / missing collection: keep whatever we have.
        console.warn('[uiStrings] subscription unavailable, using compiled strings:', err && err.code);
        if (status !== 'cache') status = 'error';
      }
    );
  } catch (e) {
    if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
    console.warn('[uiStrings] could not subscribe, using compiled strings:', e && e.message);
    status = 'error';
  }
}

/**
 * Tear down the subscription. Only used by tests — the app keeps it for the
 * whole session.
 */
export function stopUiStrings() {
  if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
  if (typeof unsubscribe === 'function') {
    try { unsubscribe(); } catch { /* ignore */ }
  }
  unsubscribe = null;
  started = false;
  status = 'idle';
}

/** Current catalogue status: idle|cache|live|error|timeout */
export function getUiStringsStatus() {
  return status;
}

/** Monotonic version counter — the useSyncExternalStore snapshot. */
export function getUiStringsVersion() {
  return version;
}

/** Subscribe to override changes. Returns an unsubscribe function. */
export function subscribeUiStrings(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// ─── Lookup ───────────────────────────────────────

/**
 * Resolve a UI string.
 *
 * Order:
 *   1. Firestore override in the farmer's OWN language (exact code, then base)
 *   2. compiled T in the farmer's OWN language
 *   3. Firestore override further down the fallback chain (usually English)
 *   4. compiled T further down the chain, then T.en
 *   5. the key itself
 *
 * Steps 1-2 before 3-4 is deliberate: right language beats fresher wording.
 * An admin edit lands in `value` (English) first and the other 15 languages
 * arrive moments later from the translation trigger; during that window a
 * Bengali farmer should keep seeing the compiled Bengali string, not English.
 *
 * Returns a string, always. Never throws.
 *
 * @param {string} namespace  e.g. 'nav'
 * @param {string} key        e.g. 'dashboard' or 'severity.mild'
 * @param {string} [lang]     defaults to the current language
 * @returns {string}
 */
export function getString(namespace, key, lang) {
  if (typeof namespace !== 'string' || typeof key !== 'string') return '';
  const code = lang || getLanguage();

  try {
    const base = getBaseCode(code);
    const bucket = overrides[namespace];
    const entry = bucket && typeof bucket === 'object' ? bucket[key] : null;
    const hasEntry = entry && typeof entry === 'object';

    if (hasEntry) {
      for (const c of [code, base]) {
        const hit = entry[c];
        if (typeof hit === 'string' && hit) return hit;
      }
    }

    const own = walk(T[code] || T[base], namespace, key);
    if (typeof own === 'string' && own) return own;

    const chain = getFallbackChain(code);

    if (hasEntry) {
      for (const c of chain) {
        const hit = entry[c];
        if (typeof hit === 'string' && hit) return hit;
      }
    }

    for (const c of chain) {
      const alt = walk(T[c], namespace, key);
      if (typeof alt === 'string' && alt) return alt;
    }

    const en = walk(T.en, namespace, key);
    if (typeof en === 'string' && en) return en;
  } catch (e) {
    console.error('[uiStrings] getString failed:', namespace, key, e);
  }

  // Never blank: showing the key beats showing nothing.
  return key;
}

// ─── Reverse index: English text -> catalogue key ──
// AppContext's legacy `t(hiText, enText)` only carries Hindi and English.
// To serve a Tamil user we look the caller's English literal back up in the
// catalogue and return the Tamil value for the key it belongs to.

// First writer wins, so the earliest namespace in T.en owns a duplicated
// English literal. In this catalogue duplicates are synonyms ("Share" exists
// in both `common` and `cropDoctor`), so either entry is correct.
let reverseIndex = null;   // exact,   trimmed English -> [ns, key]
let reverseLower = null;   // relaxed, lowercased      -> [ns, key]

const MAX_INDEX_DEPTH = 3;

function indexNode(node, namespace, path, depth) {
  if (depth > MAX_INDEX_DEPTH || node === null || node === undefined) return;

  if (typeof node === 'string') {
    const text = node.trim();
    if (!text || !path) return;
    const ref = [namespace, path];
    if (!reverseIndex.has(text)) reverseIndex.set(text, ref);
    const lower = text.toLowerCase();
    if (!reverseLower.has(lower)) reverseLower.set(lower, ref);
    return;
  }

  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i += 1) {
      indexNode(node[i], namespace, path ? `${path}.${i}` : String(i), depth + 1);
    }
    return;
  }

  if (typeof node === 'object') {
    for (const k of Object.keys(node)) {
      indexNode(node[k], namespace, path ? `${path}.${k}` : k, depth + 1);
    }
  }
}

function buildReverseIndex() {
  reverseIndex = new Map();
  reverseLower = new Map();
  const en = T.en;
  if (!en || typeof en !== 'object') return;
  for (const namespace of Object.keys(en)) {
    indexNode(en[namespace], namespace, '', 0);
  }
}

/**
 * Find the catalogue entry whose English source text equals `enText`.
 * @param {string} enText
 * @returns {{ namespace: string, key: string }|null}
 */
export function findKeyByEnglish(enText) {
  if (typeof enText !== 'string' || !enText) return null;
  if (!reverseIndex) buildReverseIndex();

  const text = enText.trim();
  if (!text) return null;

  const ref = reverseIndex.get(text) || reverseLower.get(text.toLowerCase());
  if (!ref) return null;

  return { namespace: ref[0], key: ref[1] };
}

/**
 * Translate a raw English literal into `lang` via the catalogue.
 * Returns null when the literal is not a known catalogue string — the caller
 * decides what to fall back to.
 *
 * @param {string} enText
 * @param {string} lang
 * @returns {string|null}
 */
export function resolveEnglishText(enText, lang) {
  const hit = findKeyByEnglish(enText);
  if (!hit) return null;
  const value = getString(hit.namespace, hit.key, lang);
  // getString returns the key when nothing matched — that is not a translation.
  if (!value || value === hit.key) return null;
  return value;
}

// ─── Hooks ────────────────────────────────────────

/**
 * Reactive UI string for the current language. Re-renders when the language
 * changes or when an admin edits the catalogue.
 *
 * @param {string} namespace
 * @param {string} key
 * @returns {string}
 */
export function useUiString(namespace, key) {
  const lang = useSyncExternalStore(subscribeLanguage, getLanguage, getLanguage);
  useSyncExternalStore(subscribeUiStrings, getUiStringsVersion, getUiStringsVersion);
  return getString(namespace, key, lang);
}

/**
 * Namespace-scoped getter: `const s = useUiStrings('nav'); s('prices')`.
 * @param {string} namespace
 * @returns {(key: string) => string}
 */
export function useUiStrings(namespace) {
  const lang = useSyncExternalStore(subscribeLanguage, getLanguage, getLanguage);
  const rev = useSyncExternalStore(subscribeUiStrings, getUiStringsVersion, getUiStringsVersion);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback((key) => getString(namespace, key, lang), [namespace, lang, rev]);
}

/**
 * The current language, re-rendering the caller when the language changes OR
 * when the Firestore catalogue updates. Used by AppContext so its legacy
 * `t(hi, en)` helper stays live on both.
 * @returns {string} language code
 */
export function useLiveLanguage() {
  const lang = useSyncExternalStore(subscribeLanguage, getLanguage, getLanguage);
  useSyncExternalStore(subscribeUiStrings, getUiStringsVersion, getUiStringsVersion);
  return lang;
}
